/**
 * InteriorMarkerSyncModule — ALWAYS-ON: turns interior_marker rows into
 * ENTERABLE buildings for every visitor. An owner flags a freeform part or an
 * admin placement in Edit Mode; here the object's rendered bbox becomes an
 * EnterableBuilding (the procedural shell + door + colliders come from the
 * existing interior lib), registered under `mark:<kind>:<id>` so the door
 * markers, E-to-enter, clamping and furnishing paths all light up unchanged.
 * Meshes load asynchronously, so registration retries at 1 Hz until each
 * marked object has a mesh; deleted markers unregister (exiting first).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { enterables, type EnterableBuilding } from '../lib/building_interiors.js';
import type { FreeformBuildModule } from './FreeformBuildModule.js';
import type { AtlasAdminEditorModule } from './AtlasAdminEditorModule.js';

const MIN_W = 3, MIN_H = 2.6, MIN_D = 3; // smaller than this has no room for a shell

export class InteriorMarkerSyncModule implements IModule {
  readonly name = 'InteriorMarkerSync';

  private sub: { unsubscribe(): void } | null = null;
  private subWorld: bigint | null = null;
  private acc = 0;
  private bound = false;

  constructor(private readonly freeform: FreeformBuildModule, private readonly admin: AtlasAdminEditorModule) {}

  init(app: App): void {
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:world-cleared', () => this.unregisterAll());
    bus.on('atlas:account-ready', () => this.bind());
    app.world.addSystem(makeSystem('InteriorMarkers', Phase.Animation, 41, (dt) => { this.acc += dt; if (this.acc >= 1) { this.acc = 0; this.sync(); } }));
  }

  private bind(): void {
    if (this.bound) return;
    this.bound = true;
    this.onWorld();
  }

  private onWorld(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null || this.subWorld === wid) return;
    const prev = this.sub;
    this.subWorld = wid;
    this.unregisterAll();
    try {
      this.sub = conn.subscriptionBuilder()
        .onApplied(() => { if (prev) prev.unsubscribe(); this.sync(); })
        .subscribe([`SELECT * FROM interior_marker WHERE world_id = ${wid}`]);
    } catch { /* pre-publish server */ }
  }

  /** Register every marked object that has a mesh; drop stale registrations. */
  private sync(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const wanted = new Set<string>();
    try {
      for (const m of conn.db.interiorMarker.iter()) {
        if (m.worldId !== wid) continue;
        const id = `mark:${m.refKind}:${m.refId.toString()}`;
        wanted.add(id);
        if (enterables.has(id)) continue;
        const ent = this.build(id, m.refKind, m.refId.toString());
        if (ent) enterables.set(id, ent);
      }
    } catch { return; }
    for (const id of [...enterables.keys()]) {
      if (id.startsWith('mark:') && !wanted.has(id)) enterables.delete(id);
    }
  }

  private build(id: string, refKind: string, refId: string): EnterableBuilding | null {
    let mesh: THREE.Object3D | null = null;
    let hide: () => void = () => {};
    let restore: () => void = () => {};
    if (refKind === 'part') {
      mesh = this.freeform.partMesh(refId) ?? null;
      hide = () => this.freeform.setPartHidden(refId, true);
      restore = () => this.freeform.setPartHidden(refId, false);
    } else if (refKind === 'placement') {
      mesh = this.admin.placementMesh(refId);
      hide = () => this.admin.setPlacementHidden(refId, true);
      restore = () => this.admin.setPlacementHidden(refId, false);
    }
    if (!mesh) return null;
    mesh.updateWorldMatrix(true, true);
    const bb = new THREE.Box3().setFromObject(mesh);
    const w = bb.max.x - bb.min.x, h = bb.max.y - bb.min.y, d = bb.max.z - bb.min.z;
    if (!(w >= MIN_W && h >= MIN_H && d >= MIN_D)) return null;
    const center = bb.getCenter(new THREE.Vector3());
    const yaw = new THREE.Euler().setFromQuaternion(mesh.getWorldQuaternion(new THREE.Quaternion()), 'YXZ').y;
    return {
      id,
      name: refKind === 'part' ? `structure #${refId}` : `placement #${refId}`,
      key: `mark:${refKind}`,
      x: center.x, z: center.z, yaw, groundY: bb.min.y,
      w, h, d,
      hide, restore,
    };
  }

  private unregisterAll(): void {
    for (const id of [...enterables.keys()]) if (id.startsWith('mark:')) enterables.delete(id);
  }

  dispose(): void {
    this.sub?.unsubscribe();
    this.unregisterAll();
  }
}
